import React, { useContext, useEffect, useMemo } from 'react';
import { Logo } from '@design-system';
import { WLContext } from '@contexts';
import { AuthPageContext } from '../context';

interface AuthLogoProps {
  /**
   * Custom height for the logo
   */
  height?: string;
  /**
   * Custom width for the logo
   */
  width?: string;
  /**
   * Custom logo component or source
   */
  logo?: any;
  /**
   * If true, it will not teleport and will render directly
   */
  isStatic?: boolean;
}

/**
 * A teleporting component that renders the brand logo 
 * into the fixed logo slot of the AuthPages layout.
 */
export const AuthLogo: React.FC<AuthLogoProps> = ({
  height = '42px',
  width = '224px',
  logo: customLogo,
  isStatic = false,
}) => {
  const { logo: contextLogo } = useContext(WLContext);
  const authPageContext = useContext(AuthPageContext);

  const activeLogo = customLogo || contextLogo;

  const content = useMemo(() => (
    <Logo height={height} logo={activeLogo} width={width} />
  ), [height, width, activeLogo]);

  // If we are inside the AuthPageContext, we "teleport" the content to the logo slot
  useEffect(() => {
    if (authPageContext && !isStatic) {
      authPageContext.setLogo(content);
      // Cleanup on unmount
      return () => authPageContext.setLogo(null);
    }
  }, [content, authPageContext, isStatic]);

  // If we have the context and we are not in static mode, 
  // we return null because the content is rendered in the slot
  if (authPageContext && !isStatic) {
    return null;
  }

  return content;
};
